import type { TrainingStartDto } from '@penalty-cup/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api.js';
import { haptic } from '../haptics.js';
import { useApp } from '../store.js';
import { useBackButton } from '../telegram-back.js';

interface Pop {
  id: number;
  text: string;
  x: number;
  bonus?: boolean;
}

/**
 * Training Center: футболист набивает мяч — нажатия копятся и уходят на сервер пачками; серебро, серия и
 * энергия — только с сервера (клиент лишь показывает «+1»).
 */
export function Training() {
  const token = useApp((s) => s.token);
  const me = useApp((s) => s.me);
  const setView = useApp((s) => s.setView);
  const setCoins = useApp((s) => s.setCoins);
  const buyTicket = useApp((s) => s.buyTicket);
  const play = useApp((s) => s.play);
  const back = useCallback(() => setView(null), [setView]);
  useBackButton(back);

  const [cfg, setCfg] = useState<TrainingStartDto | null>(null);
  const [coins, setLocalCoins] = useState(0);
  const [combo, setCombo] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [daily, setDaily] = useState(0);
  const [pops, setPops] = useState<Pop[]>([]);
  const [kick, setKick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [bought, setBought] = useState(false);
  // Нажатия, ещё не подтверждённые сервером — чтобы энергия на экране убывала сразу
  const [spent, setSpent] = useState(0);

  const session = useRef<string | null>(null);
  const pending = useRef({ taps: 0, first: 0, last: 0 });
  const sending = useRef(false);
  const popId = useRef(0);
  const batchNo = useRef(0);

  const start = useCallback(async () => {
    if (!token) return;
    try {
      const source = useApp.getState().trainingSource;
      useApp.setState({ trainingSource: 'home' });
      const s = await api.trainingStart(token, source);
      session.current = s.sessionId;
      setCfg(s);
      setLocalCoins(s.coins);
      setEnergy(s.energy);
      setDaily(s.dailyEarned);
      setError(null);
    } catch {
      setError('Тренировка не началась. Проверьте связь');
    }
  }, [token]);

  const popup = useCallback((text: string, x: number, bonus = false) => {
    const id = ++popId.current;
    setPops((list) => [...list.slice(-8), { id, text, x, bonus }]);
    setTimeout(() => setPops((list) => list.filter((p) => p.id !== id)), 800);
  }, []);

  const flush = useCallback(async () => {
    const p = pending.current;
    if (!token || !session.current || sending.current || p.taps === 0) return;
    sending.current = true;
    const batch = {
      taps: p.taps,
      durationMs: Math.max(0, Math.round(p.last - p.first)),
      id: `${session.current.slice(-8)}-${++batchNo.current}`,
    };
    pending.current = { taps: 0, first: 0, last: 0 };
    try {
      const r = await api.trainingTaps(token, session.current, batch.taps, batch.durationMs, batch.id);
      setSpent((n) => Math.max(0, n - batch.taps));
      setLocalCoins(r.coins);
      setCoins(r.coins);
      setCombo(r.combo);
      setEnergy(r.energy);
      setDaily(r.dailyEarned);
      if (r.bonus > 0) popup(`Серия! +${r.bonus} 🪙`, 120, true);
    } catch (err) {
      setSpent(pending.current.taps);
      // Сессия закрылась (долгая пауза) — начинаем новую
      if (err instanceof ApiError && err.message === 'session_closed') await start();
    } finally {
      sending.current = false;
    }
  }, [token, setCoins, start, popup]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- загрузка сессии при открытии экрана
    void start();
  }, [start]);

  useEffect(() => {
    if (!cfg) return;
    const id = setInterval(() => void flush(), cfg.batchMs);
    return () => clearInterval(id);
  }, [cfg, flush]);

  // Ушли с экрана — досылаем нажатия и закрываем сессию
  useEffect(
    () => () => {
      const id = session.current;
      if (!token || !id) return;
      const p = pending.current;
      const done = () => api.trainingFinish(token, id).catch(() => undefined);
      if (p.taps > 0)
        void api
          .trainingTaps(
            token,
            id,
            p.taps,
            Math.round(p.last - p.first),
            `${id.slice(-8)}-${++batchNo.current}`,
          )
          .then(
            (r) => setCoins(r.coins),
            () => undefined,
          )
          .finally(done);
      else void done();
    },
    [token, setCoins],
  );

  if (!me) return null;
  const price = cfg?.ticketPrice ?? me.tickets.price;
  const shownEnergy = Math.max(0, energy - spent);
  const empty = !!cfg && shownEnergy <= 0;
  const capped = !!cfg && daily >= cfg.dailyCap;
  const ready = coins >= price;

  const tap = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!cfg || capped) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (empty) {
      popup('нет энергии', e.clientX - rect.left);
      return;
    }
    const now = performance.now();
    const p = pending.current;
    if (p.taps === 0) p.first = now;
    p.taps += 1;
    p.last = now;
    haptic('tap');
    setSpent((n) => n + 1);
    setKick((k) => k + 1);
    popup(`+${cfg.silverPerTap}`, e.clientX - rect.left);
  };

  return (
    <main className="screen training">
      <h1 className="screen__title">Training Center</h1>
      <p className="training__coins">🪙 {coins}</p>
      {ready ? (
        <div className="training__goal training__goal--ready">
          <b>{bought ? 'Билет куплен!' : 'Билет готов!'}</b>
          {bought ? (
            <button type="button" className="play play--small" onClick={() => void play(8)}>
              Играть турнир
            </button>
          ) : (
            <button
              type="button"
              className="play play--small"
              onClick={() => void buyTicket().then(() => setBought(true))}
            >
              Купить билет за {price} 🪙
            </button>
          )}
        </div>
      ) : (
        <div className="training__goal">
          <div className="training__goal-bar" aria-hidden="true">
            <span style={{ width: `${Math.min(100, (coins / price) * 100)}%` }} />
          </div>
          <span>
            {coins} / {price} — до билета ещё {price - coins} 🪙
          </span>
        </div>
      )}
      {error && (
        <p className="field__hint field__hint--error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className={`training__pitch ${empty ? 'training__pitch--tired' : ''}`}
        onPointerDown={tap}
        disabled={!cfg || capped}
        aria-label="Тренироваться"
      >
        <span className="training__player" aria-hidden="true">
          🏃
        </span>
        <span key={kick} className="training__ball" aria-hidden="true">
          ⚽
        </span>
        {pops.map((p) => (
          <span
            key={p.id}
            className={`training__pop ${p.bonus ? 'training__pop--bonus' : ''}`}
            style={{ left: p.x }}
            aria-hidden="true"
          >
            {p.text}
          </span>
        ))}
        <span className="training__cta">
          {capped
            ? 'На сегодня всё'
            : empty
              ? `Энергия закончилась — +1 каждые ${cfg.energyRegenSeconds} с`
              : 'Жми — набивай мяч'}
        </span>
      </button>
      {cfg && (
        <div className="training__meters">
          <span className={`training__combo ${combo >= 10 ? 'training__combo--on' : ''}`}>Серия {combo}</span>
          <span className="training__stamina" aria-label="Энергия">
            ⚡ {shownEnergy}
            <i>
              <b style={{ width: `${(shownEnergy / cfg.energyMax) * 100}%` }} />
            </i>
          </span>
        </div>
      )}
      {cfg && (
        <p className="field__hint">
          {capped
            ? `Сегодня натренировано ${daily} 🪙 — приходите завтра`
            : `Серия без пауз: ${cfg.comboRules.map((r) => `${r.at} → +${r.bonus}`).join(', ')}`}
        </p>
      )}
    </main>
  );
}
